import type { ProcessImageParameters } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import { type ColorOption, ColorTone } from '@ai-color-modules/simulation/ColorPalette/colorOptions'

/**
 * r/g/b (0-255) を #RRGGBB に変換する.
 * カラーカタログ（availableColors）から hex が引けない履歴色を、画像 Parameters の
 * r/g/b から表示するためのフォールバックに使う.
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
  return `#${[r, g, b]
    .map(n => clamp(n).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`
}

/**
 * 結果画像の ProcessImageParameters から、色コード → ColorOption を合成する.
 *
 * 履歴色（プリセット外の過去使用色）はカラーカタログ（ListCompanyColors /
 * ListSharedCompanyColors）が返さなくなったため、カタログ lookup では解決できない.
 * その画像自身が持つ Parameters（main/sub area の color code + r/g/b）から最小限の
 * ColorOption を組み立て、アサイン表示のフォールバックに使う.
 *
 * - value / label: 色コード（label は表示名が無いためコード自体で代替）
 * - color: rgbToHex(r, g, b)
 * - r / g / b: Parameters の値をそのまま
 * - manufacturer: 履歴色には情報が無いため空文字
 * - tone: 履歴色にはトーン情報が無いため UNSPECIFIED（トーン絞り込みには乗らない）
 *
 * color が空のエリア（色未指定・素材のみ等）は対象外.
 * 同一コードが複数エリアに現れた場合は最初の 1 件を採用する.
 */
export const buildColorOptionsFromParameters = (
  parameters: ProcessImageParameters | null | undefined,
): ColorOption[] => {
  if (!parameters) return []

  const byCode = new Map<string, ColorOption>()

  const add = (code: string, r: number, g: number, b: number) => {
    if (!code || byCode.has(code)) return
    byCode.set(code, {
      // DB 由来でない合成オプションは負の id を採番する（React key に id を使うため。
      // 正の DB サロゲートキーと決して衝突しない）。
      id: BigInt(-(byCode.size + 1)),
      value: code,
      label: code,
      color: rgbToHex(r, g, b),
      manufacturer: '',
      tone: ColorTone.UNSPECIFIED,
      r,
      g,
      b,
    })
  }

  for (const mainArea of parameters.mainAreas ?? []) {
    add(mainArea.color, mainArea.r, mainArea.g, mainArea.b)
  }
  for (const subArea of parameters.subAreas ?? []) {
    add(subArea.color, subArea.r, subArea.g, subArea.b)
  }

  return [...byCode.values()]
}

/**
 * カラーカタログ（catalog）を優先しつつ、catalog に存在しない色コードだけを
 * fallback（画像 Parameters 由来の合成オプション）で補完する.
 *
 * 履歴色のアサイン表示で「カタログ lookup → 画像 Parameters フォールバック」の順に
 * 解決するために使う. catalog にある色は必ず catalog 側の情報（正式な label / manufacturer 等）を保つ.
 */
export const mergeColorOptionsWithFallback = (
  catalog: ColorOption[],
  fallback: ColorOption[],
): ColorOption[] => {
  const existing = new Set(catalog.map(option => option.value))
  const supplement = fallback.filter(option => !existing.has(option.value))
  if (supplement.length === 0) return catalog
  return [...catalog, ...supplement]
}
